import React, { useState } from 'react';
import { Calendar, Users, Settings, Trophy, ChevronRight, Check, Flag } from 'lucide-react';
import { MatchplayFormat, CreateTournamentRequest, CreatePlayerRequest } from '../../../types/matchplay-v2';
import { MatchplayDatabase } from '../../../lib/matchplay-v2/database';
import { courses as defaultCourses } from '../../../data/courses';
import { FormatSelector } from './FormatSelector';
import { PlayerPairing } from './PlayerPairing';
import { MatchGeneration } from './MatchGeneration';

interface MatchplayTournamentSetupProps {
  onTournamentCreated?: (tournamentId: string) => void;
  onCancel?: () => void;
}

export function MatchplayTournamentSetup({ onTournamentCreated, onCancel }: MatchplayTournamentSetupProps) {
  const [currentStep, setCurrentStep] = useState<'details' | 'format' | 'players' | 'matches'>('details');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tournament details
  const [tournamentName, setTournamentName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState('');
  const [courseId, setCourseId] = useState(defaultCourses[0]?.id || '');
  const [selectedTee, setSelectedTee] = useState(defaultCourses[0]?.tees[0]?.name || '');

  // Format selection
  const [selectedFormat, setSelectedFormat] = useState<MatchplayFormat>('singles');

  // Players and tournament state
  const [tournamentId, setTournamentId] = useState<string | null>(null);
  const [players, setPlayers] = useState<CreatePlayerRequest[]>([]);

  const steps = [
    { id: 'details', label: 'Tournament Details', icon: Settings },
    { id: 'format', label: 'Match Format', icon: Trophy },
    { id: 'players', label: 'Player Setup', icon: Users },
    { id: 'matches', label: 'Generate Matches', icon: ChevronRight }
  ];

  const handleCreateTournament = async () => {
    if (!tournamentName || !startDate || !courseId) {
      setError('Tournament name, start date, and course are required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request: CreateTournamentRequest = {
        name: tournamentName,
        format: selectedFormat,
        courseId,
        startDate,
        endDate: endDate || undefined,
        description: description || undefined
      };

      const tournament = await MatchplayDatabase.createTournament(request);
      setTournamentId(tournament.id);
      setCurrentStep('format');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tournament');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCourseSelection = (newCourseId: string) => {
    setCourseId(newCourseId);
    // Auto-select first tee when course changes
    const course = defaultCourses.find(c => c.id === newCourseId);
    if (course && course.tees.length > 0) {
      setSelectedTee(course.tees[0].name);
    }
  };

  const handleFormatSelection = async (format: MatchplayFormat) => {
    setSelectedFormat(format);
    
    // Update tournament format in database if it changed
    if (tournamentId && format !== selectedFormat) {
      try {
        await MatchplayDatabase.updateTournamentFormat(tournamentId, format);
      } catch (error) {
        console.error('Failed to update tournament format:', error);
        // Continue anyway - format will be updated when matches are generated
      }
    }
    
    setCurrentStep('players');
  };

  const handlePlayersSetup = async (playerList: CreatePlayerRequest[]) => {
    if (!tournamentId) return;

    setIsLoading(true);
    setError(null);

    try {
      await MatchplayDatabase.addPlayers(tournamentId, playerList);
      setPlayers(playerList);
      setCurrentStep('matches');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add players');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMatchGeneration = async () => {
    if (!tournamentId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Ensure tournament format is updated in database before generating matches
      await MatchplayDatabase.updateTournamentFormat(tournamentId, selectedFormat);
      await MatchplayDatabase.generateMatches(tournamentId, selectedFormat);
      await MatchplayDatabase.updateTournamentStatus(tournamentId, 'active');
      onTournamentCreated?.(tournamentId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate matches');
    } finally {
      setIsLoading(false);
    }
  };

  const getStepStatus = (stepId: string) => {
    const stepIndex = steps.findIndex(s => s.id === stepId);
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Matchplay Tournament</h1>
        <p className="text-gray-600">Set up a new matchplay tournament with singles, fourball, or foursomes format</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const status = getStepStatus(step.id);
            const Icon = step.icon;
            
            return (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  status === 'completed' 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : status === 'current'
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'border-gray-300 text-gray-400'
                }`}>
                  {status === 'completed' ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  status === 'current' ? 'text-primary-600' : 'text-gray-500'
                }`}>
                  {step.label}
                </span>
                {index < steps.length - 1 && (
                  <ChevronRight className="w-4 h-4 mx-4 text-gray-400" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {currentStep === 'details' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold mb-4">Tournament Details</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tournament Name *
              </label>
              <input
                type="text"
                value={tournamentName}
                onChange={(e) => setTournamentName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., HVGA Spring Matchplay Championship"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Optional tournament description..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course *
                </label>
                <select
                  value={courseId}
                  onChange={(e) => handleCourseSelection(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {defaultCourses.map(course => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tee *
                </label>
                <select
                  value={selectedTee}
                  onChange={(e) => setSelectedTee(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {defaultCourses.find(course => course.id === courseId)?.tees.map(tee => (
                    <option key={tee.name} value={tee.name}>{tee.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTournament}
                disabled={isLoading || !tournamentName || !startDate || !courseId}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating...' : 'Create Tournament'}
              </button>
            </div>
          </div>
        )}

        {currentStep === 'format' && (
          <FormatSelector 
            selectedFormat={selectedFormat}
            onFormatSelect={handleFormatSelection}
          />
        )}

        {currentStep === 'players' && tournamentId && (
          <PlayerPairing 
            tournamentId={tournamentId}
            format={selectedFormat}
            onPlayersSetup={handlePlayersSetup}
            isLoading={isLoading}
          />
        )}

        {currentStep === 'matches' && tournamentId && (
          <MatchGeneration 
            tournamentId={tournamentId}
            format={selectedFormat}
            players={players}
            onGenerateMatches={handleMatchGeneration}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
} 